import { DeleteOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, InputNumber, Modal, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type { Dispatch, SetStateAction } from "react";
import { deleteAttachment } from "../../api/attachmentApi";
import { BankAccountGroupedLookup } from "../lookup/BankAccountGroupedLookup";
import { SmartLookupField } from "../lookup/SmartLookupField";
import { UI_DATE_FORMAT, todayInput } from "../utils/date";
import { BulkAttachmentCell } from "./BulkAttachmentCell";
import type { BulkDailyEntryRow } from "./bulkTypes";
import { PersistedTable } from "../components/PersistedTable";

const monthOptions = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
].map((value) => ({ value, label: value }));

export function createDailyEntryRow(): BulkDailyEntryRow {
  const monthName = dayjs().format("MMMM");
  return {
    key: crypto.randomUUID(),
    entryDate: todayInput(),
    paidDate: null,
    transactionType: "Debit",
    paymentType: "Cash",
    amount: null,
    expenseMonthPayment: monthName,
    paymentStatus: "Paid"
  };
}

export function hasBulkDailyEntryRowData(row: BulkDailyEntryRow) {
  const defaultMonth = dayjs().format("MMMM");
  return !!(
    row.entryDate !== todayInput() ||
    row.paidDate ||
    row.paidOfId ||
    row.paymentOfId ||
    row.entryCategoryId ||
    row.shortRemarkId ||
    row.longRemarks?.trim() ||
    row.bankId ||
    row.bankAccountId ||
    row.paymentReference?.trim() ||
    row.amount !== null && row.amount !== undefined ||
    row.paymentType === "Bank" ||
    row.transactionType !== "Debit" ||
    row.paymentStatus !== "Paid" ||
    (row.expenseMonthPayment && row.expenseMonthPayment !== defaultMonth) ||
    row.billAttachmentIds?.length ||
    row.voucherAttachmentIds?.length ||
    row.billAttachments?.length ||
    row.voucherAttachments?.length
  );
}

export function BulkEntryGrid({
  rows,
  setRows,
  invalidRowKeys
}: {
  rows: BulkDailyEntryRow[];
  setRows: Dispatch<SetStateAction<BulkDailyEntryRow[]>>;
  invalidRowKeys?: Set<string>;
}) {
  const update = (key: string, patch: Partial<BulkDailyEntryRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
  };
  const updatePaymentType = (row: BulkDailyEntryRow, paymentType: "Cash" | "Bank") => {
    update(row.key, paymentType === "Cash"
      ? { paymentType, bankId: null, bankLabel: undefined, bankAccountId: null, bankAccountLabel: undefined, bankAccountNumber: undefined, bankAccountName: undefined, paymentMode: "NA", paymentReference: null }
      : { paymentType, paymentMode: row.paymentMode && row.paymentMode !== "NA" ? row.paymentMode : "UPI" });
  };
  const updatePaidOf = (row: BulkDailyEntryRow, value: { id: string | number | null; label: string }) => {
    const first = rows[0];
    setRows((current) => current.map((item, index) => {
      if (item.key === row.key) return { ...item, paidOfId: value.id, paidOfLabel: value.label };
      if (row.key === first?.key && !item.paidOfId && index > 0) return { ...item, paidOfId: value.id, paidOfLabel: value.label };
      return item;
    }));
  };
  const removeNow = (key: string) => {
    const row = rows.find((item) => item.key === key);
    const serverIds = [
      ...(row?.billAttachments ?? []),
      ...(row?.voucherAttachments ?? [])
    ].filter((item) => item.status === "completed" && item.serverId).map((item) => String(item.serverId));
    serverIds.forEach((id) => void deleteAttachment(id));
    setRows((current) => {
      const next = current.filter((row) => row.key !== key);
      return next.length ? next : [createDailyEntryRow()];
    });
  };
  const remove = (key: string) => {
    const row = rows.find((item) => item.key === key);
    if (!row || !hasBulkDailyEntryRowData(row)) {
      removeNow(key);
      return;
    }

    Modal.confirm({
      title: "Delete this row?",
      content: "This row has entered data. Deleting it will remove the row and its uploaded attachments from this bulk entry.",
      okText: "Delete Row",
      okButtonProps: { danger: true },
      cancelText: "Keep Row",
      onOk: () => removeNow(key)
    });
  };

  const columns: ColumnsType<BulkDailyEntryRow> = [
    { title: "#", width: 34, className: "bulk-row-number-cell", render: (_, __, index) => index + 1, fixed: "left" },
    {
      title: "",
      key: "rowActions",
      width: 38,
      className: "bulk-row-action-cell",
      fixed: "left",
      render: (_, row) => <Button danger size="small" className="bulk-row-remove" icon={<DeleteOutlined />} onClick={() => remove(row.key)} />
    },
    {
      title: "Entry Date",
      width: 150,
      render: (_, row) => <DatePicker format={UI_DATE_FORMAT} value={dayjs(row.entryDate)} onChange={(d) => update(row.key, { entryDate: d?.format("YYYY-MM-DD") ?? todayInput() })} />
    },
    {
      title: "Paid Date",
      width: 150,
      render: (_, row) => <DatePicker format={UI_DATE_FORMAT} value={row.paidDate ? dayjs(row.paidDate) : null} onChange={(d) => update(row.key, { paidDate: d?.format("YYYY-MM-DD") ?? null })} />
    },
    {
      title: "Paid Of",
      width: 240,
      render: (_, row) => <SmartLookupField lookupKey="party" value={row.paidOfId} label={row.paidOfLabel} showSearchButton={false} onChange={(v) => updatePaidOf(row, v)} />
    },
    {
      title: "Payment Of",
      width: 240,
      render: (_, row) => <SmartLookupField lookupKey="party" value={row.paymentOfId} label={row.paymentOfLabel} showSearchButton={false} onChange={(v) => update(row.key, { paymentOfId: v.id, paymentOfLabel: v.label })} />
    },
    {
      title: "Short Remark",
      width: 220,
      render: (_, row) => <SmartLookupField lookupKey="shortRemark" value={row.shortRemarkId} label={row.shortRemarkLabel} showSearchButton={false} onChange={(v) => update(row.key, { shortRemarkId: v.id, shortRemarkLabel: v.label })} />
    },
    {
      title: "Long Remark",
      width: 380,
      render: (_, row) => (
        <Input.TextArea
          value={row.longRemarks ?? ""}
          rows={3}
          placeholder="Long remarks"
          className="bulk-long-remarks"
          onChange={(e) => update(row.key, { longRemarks: e.target.value })}
        />
      )
    },
    {
      title: "Expense Month",
      width: 170,
      render: (_, row) => <Select value={row.expenseMonthPayment ?? dayjs().format("MMMM")} style={{ width: "100%" }} options={monthOptions} onChange={(v) => update(row.key, { expenseMonthPayment: v })} />
    },
    {
      title: "Entry Category",
      width: 260,
      render: (_, row) => <SmartLookupField lookupKey="entryCategory" value={row.entryCategoryId} label={row.entryCategoryLabel} parentContext={{ transactionType: row.transactionType }} showSearchButton={false} onChange={(v) => update(row.key, { entryCategoryId: v.id, entryCategoryLabel: v.label })} />
    },
    {
      title: "Amount",
      width: 140,
      align: "right",
      render: (_, row) => <InputNumber min={0} value={row.amount ?? undefined} onChange={(v) => update(row.key, { amount: Number(v ?? 0) })} style={{ width: "100%" }} />
    },
    {
      title: "Payment Type",
      width: 140,
      render: (_, row) => <Select value={row.paymentType} style={{ width: "100%" }} options={[{ value: "Cash" }, { value: "Bank" }]} onChange={(v) => updatePaymentType(row, v)} />
    },
    {
      title: "Bank A/C",
      width: 230,
      render: (_, row) => row.paymentType === "Bank" ? (
        <BankAccountGroupedLookup
          bankLabel={row.bankLabel}
          accountLabel={row.bankAccountLabel}
          accountNumber={row.bankAccountNumber}
          accountName={row.bankAccountName}
          showSearchButton={false}
          onSelect={(value) => update(row.key, {
            bankId: value.bankId,
            bankLabel: value.bankLabel,
            bankAccountId: value.bankAccountId,
            bankAccountLabel: value.bankAccountLabel,
            bankAccountNumber: value.bankAccountNumber,
            bankAccountName: value.bankAccountName
          })}
        />
      ) : <Input disabled value="" placeholder="Bank only" />
    },
    {
      title: "Payment Mode",
      width: 150,
      render: (_, row) => row.paymentType === "Bank" ? (
        <Select value={row.paymentMode ?? "UPI"} style={{ width: "100%" }} options={[{ value: "Cheque" }, { value: "UPI" }, { value: "NEFT" }, { value: "RTGS" }, { value: "IMPS" }, { value: "Card" }, { value: "Wallet" }]} onChange={(v) => update(row.key, { paymentMode: v })} />
      ) : <Input disabled value="" placeholder="Bank only" />
    },
    {
      title: "Reference No",
      width: 180,
      render: (_, row) => row.paymentType === "Bank" ? <Input value={row.paymentReference ?? ""} onChange={(e) => update(row.key, { paymentReference: e.target.value })} /> : <Input disabled value="" placeholder="Bank only" />
    },
    {
      title: "Payment Status",
      width: 150,
      render: (_, row) => <Select value={row.paymentStatus} style={{ width: "100%" }} options={[{ value: "Pending" }, { value: "Paid" }, { value: "Failed" }, { value: "Cancelled" }]} onChange={(v) => update(row.key, { paymentStatus: v })} />
    },
    {
      title: "Transaction Type",
      width: 150,
      render: (_, row) => <Select value={row.transactionType} style={{ width: "100%" }} options={[{ value: "Debit" }, { value: "Credit" }]} onChange={(v) => update(row.key, { transactionType: v })} />
    },
    { title: "Bills", width: 190, render: (_, row) => <BulkAttachmentCell row={row} type="bill" setRows={setRows} /> },
    { title: "Vouchers", width: 190, render: (_, row) => <BulkAttachmentCell row={row} type="voucher" setRows={setRows} /> },
  ];

  return (
    <div className="bulk-grid-wrap">
      <PersistedTable
        tableId="bulk-daily-entry-grid"
        rowKey="key"
        rowClassName={(row) => invalidRowKeys?.has(row.key) ? "bulk-row-invalid" : ""}
        enableColumnSettings={false}
        size="small"
        bordered
        pagination={false}
        scroll={{ x: 3050 }}
        dataSource={rows}
        columns={columns}
      />
    </div>
  );
}
